// ConfirmationModal.jsx
import React, { useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

/**
 * Confirmation Modal for LOA Transaction Generation
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls visibility
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onConfirm - Generate transaction handler
 * @param {Object} props.selectedMember - Selected member data
 * @param {Object} props.selectedProvider - Selected provider data
 * @param {Object} props.selectedCoverage - Selected coverage data
 * @param {boolean} props.isGenerating - Loading state for button
 */
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedMember,
  selectedProvider,
  selectedCoverage,
  isGenerating,
}) => {
  const playConfirmSound = useCallback(() => {
    try {
      const audio = new Audio("/mixkit-software-interface-back-2575.wav");
      audio.volume = 0.9;
      audio.play().catch(() => {});
    } catch (error) {
      // Ignore audio playback errors (autoplay restrictions, etc.)
    }
  }, []);

  const applyAckStyles = useCallback(() => {
    const popup = Swal.getPopup();
    if (!popup) return;
    let style = popup.querySelector("#ack-checkbox-style");
    if (!style) {
      style = document.createElement("style");
      style.id = "ack-checkbox-style";
      style.textContent = `
        .ack-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        .ack-checkbox input {
          transform: scale(1.2);
          margin-right: 6px;
        }
        .ack-checkbox.ack-pulse {
          background: #fff7ed;
          border-color: #fb923c;
          box-shadow: 0 0 0 2px rgba(251, 146, 60, 0.35);
          animation: ackPulse 1s ease-in-out 2;
        }
        @keyframes ackPulse {
          0% { box-shadow: 0 0 0 0 rgba(251, 146, 60, 0.55); }
          70% { box-shadow: 0 0 0 6px rgba(251, 146, 60, 0); }
          100% { box-shadow: 0 0 0 0 rgba(251, 146, 60, 0); }
        }
      `;
      popup.appendChild(style);
    }
    const checkbox = popup.querySelector(".swal2-checkbox");
    if (checkbox) {
      checkbox.classList.add("ack-checkbox");
    }
  }, []);

  const pulseAck = useCallback(() => {
    applyAckStyles();
    const popup = Swal.getPopup();
    const checkbox = popup?.querySelector(".swal2-checkbox");
    if (checkbox) {
      checkbox.classList.remove("ack-pulse");
      void checkbox.offsetWidth;
      checkbox.classList.add("ack-pulse");
    }
  }, [applyAckStyles]);

  useEffect(() => {
    if (isOpen) {
      playConfirmSound();
    }
  }, [isOpen, playConfirmSound]);

  if (!isOpen) return null;

  const progressCount = [!!selectedMember, !!selectedProvider, !!selectedCoverage].filter(Boolean).length;

  const handlePrimaryConfirm = async () => {
    playConfirmSound();
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: "You are about to issue a formal LOA. Once generated, this will affect the member's benefit limits.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, I am Sure",
      cancelButtonText: "Wait, let me check",
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#6b7280",
      customClass: {
        popup: "rounded-2xl",
        actions: "gap-3",
        confirmButton: "rounded-lg px-5 py-2.5 font-semibold",
        cancelButton: "rounded-lg px-5 py-2.5 font-semibold",
      },
      didOpen: () => {
        playConfirmSound();
      },
    });

    if (result.isConfirmed) {
      const acknowledgmentResult = await MySwal.fire({
        title: "Acknowledgment",
        icon: "info",
        input: "checkbox",
        inputValue: 0,
        inputPlaceholder:
          "I acknowledge the warning that wrongful issuance of LOA may lead to billing discrepancies and administrative sanctions.",
        showCancelButton: true,
        confirmButtonText: "I Accept Responsibility",
        cancelButtonText: "Go Back",
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#6b7280",
        customClass: {
          popup: "rounded-2xl",
          actions: "gap-3",
          confirmButton: "rounded-lg px-5 py-2.5 font-semibold",
          cancelButton: "rounded-lg px-5 py-2.5 font-semibold",
        },
        inputValidator: (value) => {
          if (value) return undefined;
          pulseAck();
          return `Please acknowledge before proceeding.<div style="font-size:12px;color:#6b7280;margin-top:4px;">You need to click the checkbox on "I acknowledge the warning..." message before proceeding.</div>`;
        },
        didOpen: () => {
          playConfirmSound();
          applyAckStyles();
        },
      });

      if (acknowledgmentResult.isConfirmed) {
        onConfirm();
      }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-y-0 left-0 tracker-aware-modal-layer bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl p-8 w-full max-w-2xl mx-4 shadow-2xl border border-gray-100 min-h-[420px] flex flex-col"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Verify LOA Details
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Please confirm that the Member, Provider, and Coverage details match the medical request.
              </p>
            </div>
            {/* <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-xl" />
            </button> */}
          </div>
          <div className="space-y-5 mb-6 flex-1">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="text-sm text-blue-900">
                  <div className="text-xs uppercase tracking-wide text-blue-600">Member</div>
                  <div className="font-semibold">{selectedMember?.text || "N/A"}</div>
                </div>
                <div className="text-sm text-blue-900">
                  <div className="text-xs uppercase tracking-wide text-blue-600">Provider</div>
                  <div className="font-semibold">{selectedProvider?.text || "N/A"}</div>
                </div>
                <div className="text-sm text-blue-900 md:col-span-2">
                  <div className="text-xs uppercase tracking-wide text-blue-600">Coverage</div>
                  <div className="font-semibold">{selectedCoverage?.text || "N/A"}</div>
                </div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${(progressCount / 3) * 100}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 text-center">
              Progress: {progressCount}/3 selections complete
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <motion.button
              whileHover={{ scale: 1.02 }}
              className="px-6 py-3 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600"
              onClick={onClose}
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
              onClick={handlePrimaryConfirm}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-2" spin />
                  Generating...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                  Details are Correct
                </>
              )}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ConfirmationModal;
